import React, { useEffect, useState } from "react";
import { Button, Image } from "react-bootstrap";
import { toast } from "react-toastify";
import AppBar from "@material-ui/core/AppBar";

import { makeStyles } from "@material-ui/core/styles";
import Toolbar from "@material-ui/core/Toolbar";
import lusukuLogoMain from "../../assets/img/lusuku_logo_main.png";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Link } from "react-router-dom";
import PasswordInput from "../../components/PasswordInput";
import { LOGIN_ROUTE } from "../../utilities/landingRoute";
import { previewResetLink, redeemResetLink } from "../../api/passwordResetApi";

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },
  menuButton: {
    marginRight: theme.spacing(2),
  },
  title: {
    flexGrow: 1,
  },
}));

// What the redeem endpoint actually enforces is min-8; the complexity rules
// below are this form's own bar. Stated UP FRONT as a checklist, because the
// old form only revealed them one failed submit at a time.
// Any character that is not a letter or digit counts as a symbol. The form used
// to accept only @$!%*?& here while its regex rejected every other character,
// so "Kampala#2026" ticked every box in the checklist and was still refused
// with "Please meet all the requirements". The checklist and the validator
// now read the same rules, so they cannot disagree again.
const SYMBOL = /[^A-Za-z0-9\s]/;
const PASSWORD_RULES = [
  { key: "length", label: "8 to 64 characters", test: (v) => v.length >= 8 && v.length <= 64 },
  { key: "upper", label: "An uppercase letter", test: (v) => /[A-Z]/.test(v) },
  { key: "lower", label: "A lowercase letter", test: (v) => /[a-z]/.test(v) },
  { key: "digit", label: "A number", test: (v) => /\d/.test(v) },
  { key: "special", label: "A symbol, for example @ # ! or .", test: (v) => SYMBOL.test(v) },
];

/** Card shell shared by the checking / dead-link / success states. */
const CenteredCard = ({ children }) => (
  <div
    style={{
      minHeight: "calc(100vh - 100px)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "32px 16px",
      background: "#f7f8fa",
    }}
  >
    <div
      style={{
        width: "100%",
        maxWidth: 480,
        background: "#fff",
        borderRadius: 12,
        border: "1px solid #e5e7eb",
        boxShadow: "0 10px 30px rgba(15, 23, 42, 0.06)",
        padding: "40px 32px",
      }}
    >
      {children}
    </div>
  </div>
);

const DEAD_LINK_COPY = {
  EXPIRED: {
    title: "This link has expired",
    body:
      "Set-password links only work for a limited time, so one sitting in an inbox can't be used by somebody else later. Your account is unchanged. Just request a fresh link.",
  },
  USED_OR_INVALID: {
    title: "This link has already been used or isn't valid",
    body:
      "Each link works exactly once. If you already set your password, simply sign in. Otherwise, request a fresh link below.",
  },
  DEACTIVATED: {
    title: "This account is deactivated",
    body:
      "A password can't be set for a deactivated account. Contact your Lusuku administrator or support@lusuku.ug to reactivate it first.",
  },
};

// Shared set-password form. Defaults to the platform Users flow; the 3PL
// staff route reuses it with a different redeem endpoint (see App.js:
// /3pl-reset-password/:token/:id → endpointBase="/api/3pl-staff/reset-password").
//
// Serves BOTH first-time invites (a freshly provisioned organizer, vendor or
// staff account whose only way in is this link) and true resets — so the copy
// says "set your password", never "reset", and names (masked) whose account it
// is. Before rendering the form it asks the server whether the link is even
// redeemable, so an expired link is a clear explanation with a way forward
// instead of a dead-end toast after the password was typed twice.
const AgentPasswordResetForm = ({ match, endpointBase = "/user/reset-password" }) => {
  const classes = useStyles();
  const [resetStatus, setResetStatus] = useState("");
  const [success, setSuccess] = useState(false);

  const user_id = match?.params?.id;
  const resetToken = match?.params?.token;

  // Link preview: null = still checking, {valid:true, email, expiresAt} or
  // {valid:false, reason}. Only the platform Users flow has the preview
  // endpoint; the 3PL form skips straight to the form as before.
  const canPreview = endpointBase === "/user/reset-password";
  const [preview, setPreview] = useState(canPreview ? null : { valid: true, email: null });

  useEffect(() => {
    if (!canPreview) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await previewResetLink({ userId: user_id, resetToken });
        if (!cancelled) setPreview(data && typeof data.valid === "boolean" ? data : { valid: true, email: null });
      } catch (e) {
        // Server unreachable for the preview — let the form render; the
        // redeem endpoint still enforces everything.
        if (!cancelled) setPreview({ valid: true, email: null });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canPreview, user_id, resetToken]);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setSubmitting(true);
      const { status, data } = await redeemResetLink({
        endpointBase,
        userId: user_id,
        resetToken,
        password: values.password,
      });
      if (status === 0) {
        toast.error("Could not reach the server. Check your connection and try again.");
        setSubmitting(false);
        return;
      }
      // 3PL endpoints wrap errors as { error: { message } }.
      const message = data.message || data?.error?.message;
      setResetStatus(message);
      if (data.success) {
        toast.success("Your password is set!");
        setSuccess(true);
        setSubmitting(false);
      } else {
        // A dead token discovered at submit time gets the full explanation
        // card (with the request-a-new-link path), not just a toast.
        const text = String(message || "");
        if (/expired/i.test(text)) {
          setPreview({ valid: false, reason: "EXPIRED" });
        } else if (/used or invalid/i.test(text)) {
          setPreview({ valid: false, reason: "USED_OR_INVALID" });
        } else if (/deactivated/i.test(text)) {
          setPreview({ valid: false, reason: "DEACTIVATED" });
        } else {
          toast.error(text || "Could not set the password. Please try again.");
        }
        setSubmitting(false);
      }
    } catch (error) {
      console.log(error);
      toast.error("Could not reach the server. Check your connection and try again.");
      setSubmitting(false);
    }
  };

  const Header = (
    <div className={classes.root}>
      <AppBar position="static" color="inherit" elevation={1}>
        <Toolbar>
          <Link to="/">
            <Image
              src={lusukuLogoMain}
              alt="Lusuku"
              style={{ width: 80, height: 80, objectFit: "contain" }}
            />
          </Link>
        </Toolbar>
      </AppBar>
    </div>
  );

  if (success) {
    return (
      <>
        {Header}
        <CenteredCard>
          <div style={{ textAlign: "center", marginBottom: 20 }}>
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                background: "#ecfdf5",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: 16,
              }}
            >
              <i
                className="fas fa-check-circle"
                style={{ fontSize: 32, color: "#16a34a" }}
              />
            </div>
            <h2
              style={{
                fontSize: 22,
                fontWeight: 700,
                color: "#111827",
                margin: 0,
              }}
            >
              Your password is set
            </h2>
            <p
              style={{
                marginTop: 10,
                color: "#4b5563",
                fontSize: 14,
                lineHeight: 1.5,
              }}
            >
              {resetStatus || "Your password has been updated."} Sign in with
              your email address and your new password.
            </p>
          </div>
          <Link to={LOGIN_ROUTE} style={{ textDecoration: "none" }}>
            <Button
              variant="success"
              size="lg"
              style={{
                width: "100%",
                fontWeight: 600,
                padding: "10px 16px",
                background: "#16a34a",
                borderColor: "#16a34a",
              }}
            >
              Proceed to Login
            </Button>
          </Link>
          <p
            style={{
              textAlign: "center",
              marginTop: 20,
              marginBottom: 0,
              fontSize: 12,
              color: "#9ca3af",
            }}
          >
            For security, you'll be asked to sign in again.
          </p>
        </CenteredCard>
      </>
    );
  }

  // Still asking the server whether the link is redeemable.
  if (preview === null) {
    return (
      <>
        {Header}
        <CenteredCard>
          <div style={{ textAlign: "center", color: "#6b7280", fontSize: 14 }}>
            <i className="fas fa-circle-notch fa-spin" style={{ fontSize: 22, marginBottom: 12 }} />
            <div>Checking your link…</div>
          </div>
        </CenteredCard>
      </>
    );
  }

  // Dead link — explain what happened and give a way forward, instead of
  // letting the person type a password twice into a form that cannot work.
  if (!preview.valid) {
    const copy = DEAD_LINK_COPY[preview.reason] || DEAD_LINK_COPY.USED_OR_INVALID;
    return (
      <>
        {Header}
        <CenteredCard>
          <div style={{ textAlign: "center", marginBottom: 20 }}>
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                background: "#fff7ed",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: 16,
              }}
            >
              <i className="fas fa-hourglass-end" style={{ fontSize: 28, color: "#ea580c" }} />
            </div>
            <h2 style={{ fontSize: 22, fontWeight: 700, color: "#111827", margin: 0 }}>
              {copy.title}
            </h2>
            <p style={{ marginTop: 10, color: "#4b5563", fontSize: 14, lineHeight: 1.6 }}>
              {copy.body}
            </p>
          </div>
          {preview.reason !== "DEACTIVATED" && (
            <>
              <Link to="/public/resetPassword" style={{ textDecoration: "none" }}>
                <Button
                  variant="success"
                  size="lg"
                  style={{
                    width: "100%",
                    fontWeight: 600,
                    padding: "10px 16px",
                    background: "#16a34a",
                    borderColor: "#16a34a",
                  }}
                >
                  Email me a new link
                </Button>
              </Link>
              <Link to={LOGIN_ROUTE} style={{ textDecoration: "none" }}>
                <Button
                  variant="outline-secondary"
                  size="lg"
                  style={{ width: "100%", fontWeight: 600, padding: "10px 16px", marginTop: 10 }}
                >
                  I already have a password. Sign in
                </Button>
              </Link>
              <p
                style={{
                  textAlign: "center",
                  marginTop: 16,
                  marginBottom: 0,
                  fontSize: 12,
                  color: "#9ca3af",
                }}
              >
                Was this an invite? The person who added you can also re-send it.
              </p>
            </>
          )}
        </CenteredCard>
      </>
    );
  }

  return (
    <>
      {Header}
      <div className="container pt-4">
        <div className="row">
          <div className="col-md-6 offset-md-3">
            <div className="card">
              <div className="card-body">
                <h1 style={{ fontSize: 30, fontWeight: 300, marginBottom: 4 }}>
                  Set your password
                </h1>
                <p style={{ color: "#6b7280", fontSize: 14, marginBottom: 18 }}>
                  {preview.email ? (
                    <>
                      You&apos;re choosing the password for{" "}
                      <strong style={{ color: "#374151" }}>{preview.email}</strong>. Nobody at
                      Lusuku can see it. It&apos;s yours alone.
                    </>
                  ) : (
                    <>Choose the password you&apos;ll use to sign in to Lusuku. Nobody at Lusuku can see it.</>
                  )}
                </p>
                <Formik
                  initialValues={{
                    password: "",
                    confirmPassword: "",
                  }}
                  validationSchema={Yup.object({
                    password: Yup.string()
                      .required("Password is required!")
                      .min(8, "Use at least 8 characters.")
                      .max(64, "Use at most 64 characters.")
                      .test(
                        "all-rules",
                        "Please meet all the requirements listed below.",
                        (v) => PASSWORD_RULES.every((rule) => rule.test(v || ""))
                      ),
                    confirmPassword: Yup.string()
                      .required("Confirm Password is required")
                      .oneOf(
                        [Yup.ref("password"), null],
                        "Passwords must match!"
                      ),
                  })}
                  onSubmit={handleSubmit}
                >
                  {({
                    values,
                    errors,
                    touched,
                    handleChange,
                    handleBlur,
                    handleSubmit,
                    isSubmitting,
                  }) => (
                    <Form onSubmit={handleSubmit}>
                      <div className="form-group">
                        <label htmlFor="password">Password</label>
                        <Field
                          as={PasswordInput}
                          id="password"
                          name="password"
                          autoComplete="new-password"
                          className={
                            "form-control" +
                            (errors.password && touched.password ? " is-invalid" : "")
                          }
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.password}
                        />
                        <ErrorMessage
                          name="password"
                          component="div"
                          className="invalid-feedback"
                          style={{ display: "block" }}
                        />
                        {/* The rules, visible BEFORE the first failed submit,
                            ticking live as each one is met. */}
                        <ul
                          style={{
                            listStyle: "none",
                            padding: "10px 0 0 2px",
                            margin: 0,
                            fontSize: 12.5,
                            color: "#6b7280",
                          }}
                        >
                          {PASSWORD_RULES.map((rule) => {
                            const met = rule.test(values.password || "");
                            return (
                              <li key={rule.key} style={{ marginBottom: 3 }}>
                                <i
                                  className={met ? "fas fa-check-circle" : "far fa-circle"}
                                  style={{
                                    color: met ? "#16a34a" : "#d1d5db",
                                    marginRight: 7,
                                    width: 14,
                                  }}
                                />
                                <span style={{ color: met ? "#374151" : undefined }}>{rule.label}</span>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                      <div className="form-group">
                        <label htmlFor="confirmPassword">
                          Confirm Password
                        </label>
                        <Field
                          as={PasswordInput}
                          id="confirmPassword"
                          name="confirmPassword"
                          autoComplete="new-password"
                          className={
                            "form-control" +
                            (errors.confirmPassword && touched.confirmPassword ? " is-invalid" : "")
                          }
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.confirmPassword}
                        />
                        <ErrorMessage
                          name="confirmPassword"
                          component="div"
                          className="invalid-feedback"
                          style={{ display: "block" }}
                        />
                      </div>
                      <div className="form-group">
                        <button
                          type="submit"
                          className="btn btn-primary btn-block"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? "Setting password…" : "Set password"}
                        </button>
                      </div>
                    </Form>
                  )}
                </Formik>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AgentPasswordResetForm;
